import { ColDef } from "@ag-grid-community/core";
import { isNumber } from "@chakra-ui/utils";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTableContext } from "shared/components/table/data/DataTableContext";
import { DataTableProps } from "shared/components/table/data/DataTableProps";
import {
  GenericTableAgGridReactProps,
  isTotalRow,
} from "shared/components/table/data/DataTableTypes";
import { DataTableAutoGroupColumnCell } from "shared/components/table/data/internal/DataTableAutoGroupColumnCell";
import { DataTableAutoGroupColumnHeader } from "shared/components/table/data/internal/DataTableAutoGroupColumnHeader";
import { COLUMN_ID_AUTO_GROUP } from "shared/components/table/data/internal/DataTableConstants";
import { dataTableHeaderClass } from "shared/components/table/data/internal/DataTableHeaderClass";
import {
  valueForCellFilter,
  valueForCellSort,
} from "shared/components/table/data/internal/DataTableInternalUtils";
import { maybernTableSuppressKeyboardEventForInput } from "shared/components/table/maybern/MaybernTableSuppressKeyboardEventForInput";
import { isMaybernTableColumnLastInGroup } from "shared/components/table/maybern/MaybernTableUtils";

export const useDataTableColumnDefsAgProps = ({
  data: { client_row_aggregation, columns },
  fillWidth,
  suppressMovable,
}: DataTableProps): GenericTableAgGridReactProps => {
  const { t } = useTranslation(["common"]);

  const autoGroupColumnInitialSort = columns.find(
    (c) => c.initial_row_group && c.initial_sort,
  )?.initial_sort;

  const defaultColDef = useMemo<ColDef<GenericTableRow, GenericTableCell>>(
    () => ({
      // the ag-grid documentation says that setting this explicitly to false
      // improves performance of column auto-sizing
      cellDataType: false,
      flex: fillWidth ? 1 : undefined,
      sortable: true,
      resizable: true,
      enableRowGroup: !!client_row_aggregation,
      suppressMovable,
      comparator,
      suppressKeyboardEvent: maybernTableSuppressKeyboardEventForInput,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      headerClass: dataTableHeaderClass,
      cellClassRules: {
        "mb-cell-last-in-group": isMaybernTableColumnLastInGroup,
      },
      filterValueGetter: (params) =>
        valueForCellFilter(params.node?.data?.cells[params.column.getId()]),
      filterParams: {
        buttons: ["reset"],
      },
    }),
    [client_row_aggregation, fillWidth, suppressMovable],
  );

  const autoGroupColumnDef = useMemo(
    (): ColDef<GenericTableRow, GenericTableCell> => ({
      colId: COLUMN_ID_AUTO_GROUP,
      headerValueGetter: (params) =>
        params.api
          .getRowGroupColumns()
          .map((col) => params.api.getDisplayNameForColumn(col, null))
          .join(" > "),
      valueGetter: (params) => {
        const tableContext = params.context as DataTableContext;
        const value = tableContext.valueGetter?.(params);
        if (value) {
          return value;
        }
        if (isTotalRow(params.node?.data)) {
          return {
            value: {
              type: "text",
              text: t("common:table.total"),
            },
          };
        }
        return undefined;
      },
      pinned: "left",
      lockPinned: true,
      lockVisible: true,
      lockPosition: true,
      suppressMovable: true,
      sortable: true,
      initialSort: autoGroupColumnInitialSort,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: DataTableAutoGroupColumnCell,
      },
      headerComponent: DataTableAutoGroupColumnHeader,
      headerClass: "mb-cell-group-column-header",
      cellClassRules: {
        "mb-cell-group-column": () => true,
        "mb-cell-is-header": () => true,
      },
    }),
    [autoGroupColumnInitialSort, t],
  );

  return {
    defaultColDef,
    autoGroupColumnDef: client_row_aggregation ? autoGroupColumnDef : undefined,
  };
};

const comparator: ColDef<GenericTableRow, GenericTableCell>["comparator"] = (
  cellA,
  cellB,
) => {
  const cellAValue = valueForCellSort(cellA);
  const cellBValue = valueForCellSort(cellB);
  if (isNumber(cellAValue) || isNumber(cellBValue)) {
    const cellANumber = isNumber(cellAValue) ? cellAValue : 0;
    const cellBNumber = isNumber(cellBValue) ? cellBValue : 0;
    return cellANumber - cellBNumber;
  }
  const cellAString = cellAValue || "";
  const cellBString = cellBValue || "";
  return cellBString.localeCompare(cellAString);
};
